import FleetModal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { FC, Fragment, useState, useEffect, useMemo } from 'react';
import { Alert, Space, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMPPSLIKResult } from '@/libs/fleet-api/services/mpp';
import AnalisaSLIKManual from './AnalisaSLIKManual';
import AnalisaSLIKRobotic from './AnalisaSLIKRobotic';

export const useKolektabilitasModal = () =>
	useAsyncModal<{
		slikInquiryId: number;
	}>();

// Helper component that only renders when data is available
const SLIKDataContent: FC<{
	slikInquiryId: number;
	onBack: () => void;
}> = ({ slikInquiryId, onBack }) => {
	// State for selected company indexes in robotic SLIK
	const [selectedCompanyIndexes, setSelectedCompanyIndexes] = useState<
		number[]
	>([]);
	const [selectedShareholderIndexes, setSelectedShareholderIndexes] = useState<
		{ secondIndex: number; referenceNumber: string }[]
	>([]);

	// Track if selected indexes were initialized from API
	const [hasInitializedIndexes, setHasInitializedIndexes] = useState(false);

	// Only call the API if we have a valid inquiry ID
	const { data: slikResultData, isFetching: isSlikResultFetching } =
		useMPPSLIKResult(
			slikInquiryId && slikInquiryId > 0 ? slikInquiryId : undefined,
		);

	// Determine SLIK type based on the data
	const detail = slikResultData?.[0];
	const prospectDetail = slikResultData?.[1];
	const isManual = Boolean(detail?.isManualSlik && detail?.manualSlikResult);
	const isRobotic = Boolean(
		detail?.roboticSlikResult && detail?.roboticSlikRequestResult,
	);

	// Memoize the details for useEffect dependency
	const details = useMemo(() => {
		if (
			slikResultData &&
			Array.isArray(slikResultData) &&
			slikResultData[0]?.roboticSlikRequestResult?.details
		) {
			return slikResultData[0].roboticSlikRequestResult.details;
		}
		return null;
	}, [slikResultData]);

	// Initialize selected indexes from API response when data is available - only once
	useEffect(() => {
		if (
			slikResultData &&
			details &&
			!hasInitializedIndexes &&
			!isSlikResultFetching
		) {
			console.log(
				'🔄 Initializing selected indexes from API response in KolektabilitasModal...',
			);
			let hasSelectedIndexes = false;

			// Set company indexes for Customers role
			const customerDetail = details.find(
				(detail) => detail.role === 'Customers',
			);
			if (customerDetail?.selectedIndexes) {
				console.log(
					'📋 Found customer selectedIndexes:',
					customerDetail.selectedIndexes,
				);
				const indexes = customerDetail.selectedIndexes
					.split(',')
					.map((index: string) => parseInt(index.trim(), 10))
					.filter((index: number) => !isNaN(index));
				console.log('✅ Parsed customer indexes:', indexes);
				setSelectedCompanyIndexes(indexes);
				hasSelectedIndexes = true;
			}

			// Set shareholder indexes for non-Customer roles that have selectedIndexes
			const shareholderDetails = details.filter(
				(detail) => detail.role !== 'Customers' && detail.selectedIndexes,
			);

			const shareholderIndexes: {
				secondIndex: number;
				referenceNumber: string;
			}[] = [];

			shareholderDetails.forEach((detail) => {
				if (detail.selectedIndexes) {
					console.log(
						'📋 Found shareholder selectedIndexes:',
						detail.selectedIndexes,
						'for role:',
						detail.role,
					);
					const indexes = detail.selectedIndexes
						.split(',')
						.map((index: string) => parseInt(index.trim(), 10))
						.filter((index: number) => !isNaN(index));

					indexes.forEach((index: number) => {
						shareholderIndexes.push({
							secondIndex: index,
							referenceNumber: detail.referenceNumber,
						});
					});
				}
			});

			if (shareholderIndexes.length > 0) {
				hasSelectedIndexes = true;
				console.log('✅ Set shareholder indexes:', shareholderIndexes);
			}

			// Batch state updates to prevent multiple re-renders
			setSelectedShareholderIndexes(shareholderIndexes);
			setHasInitializedIndexes(true);
			console.log(
				'🎯 Initialization complete in KolektabilitasModal. hasSelectedIndexes:',
				hasSelectedIndexes,
			);
		}
	}, [details, hasInitializedIndexes, isSlikResultFetching, slikResultData]);

	// Reset state when inquiry ID changes
	useEffect(() => {
		if (slikInquiryId && slikInquiryId > 0) {
			setSelectedCompanyIndexes([]);
			setSelectedShareholderIndexes([]);
			setHasInitializedIndexes(false);
		}
	}, [slikInquiryId]);

	const renderSLIKContent = () => {
		// Show error if we don't have a valid inquiry ID
		if (!slikInquiryId || slikInquiryId <= 0) {
			return (
				<div style={{ textAlign: 'center', padding: '40px' }}>
					<Alert
						message="Invalid SLIK Inquiry ID"
						description="No valid SLIK inquiry ID provided."
						type="error"
						showIcon
					/>
				</div>
			);
		}

		// Only show loading if we have a valid record and are fetching or initializing indexes
		if (
			isSlikResultFetching ||
			(!hasInitializedIndexes &&
				slikResultData &&
				detail?.roboticSlikRequestResult)
		) {
			return (
				<div style={{ textAlign: 'center', padding: '40px' }}>
					<Spin size="large" />
					<div style={{ marginTop: 16 }}>Loading SLIK data...</div>
				</div>
			);
		}

		if (isManual && detail && detail.manualSlikResult) {
			const { manualSlikResult, customerMainInformation, ccaNotes } = detail;
			return (
				<AnalisaSLIKManual
					result={manualSlikResult}
					mainInformation={customerMainInformation}
					notes={ccaNotes || ''}
					onBack={onBack}
				/>
			);
		}

		if (isRobotic && detail) {
			const { roboticSlikResult, roboticSlikRequestResult } = detail;
			const { managementSlikInformation, shareholderSlikInformation } =
				prospectDetail || {};
			return (
				<AnalisaSLIKRobotic
					data={roboticSlikResult}
					result={roboticSlikRequestResult?.details}
					managementSlikInformation={managementSlikInformation || []}
					shareholderSlikInformation={shareholderSlikInformation || []}
					selectedCompanyIndexes={selectedCompanyIndexes}
					selectedShareholderIndexes={selectedShareholderIndexes}
					onCompanyIndexesChange={setSelectedCompanyIndexes}
					onShareholderIndexesChange={setSelectedShareholderIndexes}
				/>
			);
		}

		return (
			<div style={{ textAlign: 'center', padding: '40px' }}>
				<Alert
					message="No SLIK Data Available"
					description="Tidak ada data SLIK yang tersedia untuk customer ini."
					type="info"
					showIcon
				/>
			</div>
		);
	};

	return <Fragment>{renderSLIKContent()}</Fragment>;
};

const KolektabilitasModal: FC<ReturnType<typeof useKolektabilitasModal>> = ({
	modal,
	data,
	close,
}) => {
	// console.log('data', data);
	// Handle case where data is undefined
	if (!data) {
		return (
			<FleetModal
				{...modal}
				width={1400}
				closeIcon={false}
				keyboard={false}
				maskClosable={true}
				cancelButtonProps={{ hidden: true }}
				buttonPosition="end"
				okText="CLOSE"
				cancelText="CANCEL"
				onCancel={close}
				onOk={close}
			>
				<Alert
					message="Error"
					description="Data not available. Please try again."
					type="error"
					showIcon
				/>
			</FleetModal>
		);
	}

	// Validate required fields are present
	const isValidData = data.slikInquiryId;
	if (!isValidData) {
		const missingFields = [];
		if (!data.slikInquiryId) missingFields.push('SLIK Inquiry ID');

		return (
			<FleetModal
				{...modal}
				width={1400}
				closeIcon={false}
				keyboard={false}
				maskClosable={true}
				cancelButtonProps={{ hidden: true }}
				buttonPosition="end"
				okText="CLOSE"
				cancelText="CANCEL"
				onCancel={close}
				onOk={close}
			>
				<Alert
					message="Invalid Data"
					description={`Required data is missing: ${missingFields.join(', ')}. Please ensure both Customer ID and SLIK Inquiry ID are provided.`}
					type="warning"
					showIcon
				/>
			</FleetModal>
		);
	}

	return (
		<FleetModal
			{...modal}
			width={1400}
			closeIcon={false}
			keyboard={false}
			maskClosable={true}
			buttonPosition="end"
			okText="CLOSE"
			cancelText="CANCEL"
			cancelButtonProps={{ hidden: true }}
			onCancel={close}
			onOk={close}
		>
			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<div
					style={{
						display: 'flex',
						gap: 10,
						marginBottom: 30,
					}}
				>
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={close}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
					<div style={{ flex: 1 }}>
						<FleetTitle>View Kolektabilitas</FleetTitle>
					</div>
				</div>

				{/* Show SLIK Analysis Summary first */}
				{isValidData && (
					<SLIKDataContent
						slikInquiryId={data.slikInquiryId}
						onBack={close}
					/>
				)}
			</Space>
		</FleetModal>
	);
};

export default KolektabilitasModal;
